/**
 * +acolyte-specs: bot kits for the Acolyte specialisations (docs/design/classes/specs.md "Acolyte", the
 * rotation sections). The APL presses evolved ids directly (the spec'd ClassDef binds them). Auto,
 * Group Finder strangers and the harness all play these.
 */
import { HEMOMANCER, HEMOMANCER_AURA, HERETIC, HERETIC_AURA, LIGHTBINDER, LIGHTBINDER_AURA } from '@keystone/content';
import type { ClassKit } from '../kits';

const LB = LIGHTBINDER;
const LBA = LIGHTBINDER_AURA;
const HM = HEMOMANCER;
const HMA = HEMOMANCER_AURA;
const HR = HERETIC;
const HRA = HERETIC_AURA;

/**
 * Lightbinder (Discipline-like): ramp first, heal by fighting. Keep the tank warded and the party
 * Absolved (Haloburst when fewer than three marks, Radiant Ward on whoever lacks one), then Triune Ray
 * and Dawnlash the target: Absolution turns the damage into healing on every mark. Quickmend is the
 * emergency button only (triage at 50%), Exaltation when two allies sink. Alone: Blinding Verdict opens
 * a fresh enemy and Dawnlash (Sentenced) goes ahead of Triune Ray while it reels.
 */
export const lightbinderKit: ClassKit = {
  classId: 'acolyte',
  role: 'healer',
  ranged: true,
  interrupt: LB.blindingVerdict,
  externalDefensive: LB.radiantWard,
  externalDefensiveAura: LBA.radiantWard,
  groupHeal: LB.haloburst,
  heals: { flash: LB.quickmend, manaResource: 'mana', flashAt: 0.6 },
  oocBuffs: [{ ability: LB.lightboundOath, when: [{ type: 'partyLacksAura', auraId: LBA.oath }] }],
  apl: [
    { ability: LB.exaltation, when: [{ type: 'inCombat' }, { type: 'injuredAllies', below: 0.65, atLeast: 2 }] },
    // Ramp: the tank's ward, then the party's marks.
    { ability: LB.radiantWard, target: 'tank', when: [{ type: 'inCombat' }, { type: 'tankNeedsAura', auraId: LBA.radiantWard }] },
    { ability: LB.haloburst, when: [{ type: 'inCombat' }, { type: 'partySize', atLeast: 2 }, { type: 'not', cond: { type: 'markedAllies', auraId: LBA.absolution, atLeast: 3 } }] },
    { ability: LB.radiantWard, target: 'unmarkedAlly', auraId: LBA.absolution, when: [{ type: 'inCombat' }, { type: 'partySize', atLeast: 2 }] },
    // Solo (Auto): ward yourself, so your own damage heals you.
    { ability: LB.radiantWard, target: 'self', when: [{ type: 'inCombat' }, { type: 'selfHpBelow', pct: 0.9 }, { type: 'selfLacksAura', auraId: LBA.radiantWard }] },
    // Heal by fighting.
    { ability: LB.blindingVerdict, target: 'enemy', when: [{ type: 'inCombat' }, { type: 'markedAllies', auraId: LBA.absolution, atLeast: 2 }, { type: 'injuredAllies', below: 0.75, atLeast: 2 }] },
    // Solo (Auto): open on a fresh enemy with the Verdict (it is also the kick: the kick logic takes
    // casters first), then Dawnlash ahead of Triune Ray while it is Sentenced.
    { ability: LB.blindingVerdict, target: 'enemy', when: [{ type: 'inCombat' }, { type: 'not', cond: { type: 'partySize', atLeast: 2 } }, { type: 'targetHpAbove', pct: 0.4 }] },
    { ability: LB.dawnlash, target: 'enemy', when: [{ type: 'inCombat' }, { type: 'not', cond: { type: 'partySize', atLeast: 2 } }, { type: 'targetAura', auraId: LBA.sentenced, minRemainingMs: 1400 }] },
    { ability: LB.triuneRay, target: 'enemy', when: [{ type: 'inCombat' }] },
    { ability: LB.dawnlash, target: 'enemy', when: [{ type: 'inCombat' }] },
    // On the move: keep the marks rolling (instant wards).
    { ability: LB.radiantWard, target: 'unmarkedAlly', auraId: LBA.absolution, when: [{ type: 'moving' }, { type: 'partySize', atLeast: 2 }] },
  ],
};

/**
 * Hemomancer: Transfusion on whoever is hurt (never on yourself), Scab on the tank, Crimson Bloom under
 * the tank when the group is hurt, Bleedfield on the pack; Blood Bolt refills you between heals, and
 * below 45% health you drink before pouring out more (unless an ally is below 40%). Martyr when two
 * allies drop under 60% and you have the blood to spare.
 */
export const hemomancerKit: ClassKit = {
  classId: 'acolyte',
  role: 'healer',
  ranged: true,
  interrupt: HM.coagulate,
  externalDefensive: HM.scab,
  externalDefensiveAura: HMA.scab,
  heals: { flash: HM.transfusion, manaResource: 'mana', flashNotSelf: true, flashSelfFloor: { hp: 0.45, allyBelow: 0.4 } },
  oocBuffs: [{ ability: HM.veinPact, when: [{ type: 'partyLacksAura', auraId: HMA.veinPact }] }],
  apl: [
    { ability: HM.martyr, when: [{ type: 'inCombat' }, { type: 'injuredAllies', below: 0.6, atLeast: 2 }, { type: 'not', cond: { type: 'selfHpBelow', pct: 0.55 } }] },
    { ability: HM.scab, target: 'tank', when: [{ type: 'inCombat' }, { type: 'tankNeedsAura', auraId: HMA.scab }, { type: 'not', cond: { type: 'selfHpBelow', pct: 0.4 } }] },
    { ability: HM.crimsonBloom, target: 'tank', when: [{ type: 'inCombat' }, { type: 'injuredAllies', below: 0.85, atLeast: 2 }, { type: 'not', cond: { type: 'selfHpBelow', pct: 0.4 } }] },
    { ability: HM.bleedfield, target: 'groundTarget', when: [{ type: 'inCombat' }] },
    // Solo (Auto): a scab on yourself when you sink.
    { ability: HM.scab, target: 'self', when: [{ type: 'inCombat' }, { type: 'selfHpBelow', pct: 0.5 }, { type: 'selfLacksAura', auraId: HMA.scab }] },
    { ability: HM.bloodBolt, target: 'enemy', when: [{ type: 'inCombat' }] },
  ],
};

/**
 * Heretic (Warlock-like): Rotcurse everything (the target first, then whoever lacks it), Blight Field
 * on cooldown, Call the Hollow One at 3 Tithes on anything sturdy or a pack, Soulflay at 4+ (never
 * cap 5), on a Hollow Whisper, or at 2+ while the Hollow One is far off; Unholy Tithe whenever health
 * allows. Hexbolt fills.
 */
export const hereticKit: ClassKit = {
  classId: 'acolyte',
  role: 'dps',
  ranged: true,
  interrupt: HR.dreadGaze,
  personalDefensive: HR.wraithWard,
  oocBuffs: [{ ability: HR.hollowCovenant, when: [{ type: 'partyLacksAura', auraId: HRA.covenant }] }],
  apl: [
    { ability: HR.wraithWard, when: [{ type: 'inCombat' }, { type: 'selfHpBelow', pct: 0.6 }] },
    { ability: HR.callHollowOne, target: 'enemy', when: [{ type: 'inCombat' }, { type: 'any', of: [{ type: 'targetHpAbove', pct: 0.3 }, { type: 'targets', atLeast: 3, radius: 10 }] }] },
    { ability: HR.unholyTithe, when: [{ type: 'inCombat' }, { type: 'not', cond: { type: 'selfHpBelow', pct: 0.55 } }, { type: 'resource', resource: 'tithes', below: 5 }] },
    { ability: HR.soulflay, target: 'enemy', when: [{ type: 'selfAura', auraId: HRA.hollowWhisper }] },
    { ability: HR.soulflay, target: 'enemy', when: [{ type: 'resource', resource: 'tithes', atLeast: 4 }] },
    { ability: HR.rotcurse, target: 'enemy', when: [{ type: 'targetNeedsAura', auraId: HRA.rotcurse, refreshBelowMs: 5400 }, { type: 'targetHpAbove', pct: 0.1 }] },
    { ability: HR.blightField, target: 'groundTarget', when: [{ type: 'inCombat' }] },
    { ability: HR.rotcurse, target: 'enemyLacking', auraId: HRA.rotcurse, when: [{ type: 'inCombat' }] },
    { ability: HR.soulflay, target: 'enemy', when: [{ type: 'resource', resource: 'tithes', atLeast: 2 }, { type: 'not', cond: { type: 'cooldownBelow', ability: HR.callHollowOne, ms: 12_000 } }] },
    { ability: HR.soulflay, target: 'enemy', when: [{ type: 'targetHpBelow', pct: 0.2 }, { type: 'resource', resource: 'tithes', atLeast: 1 }] },
    { ability: HR.hexbolt, target: 'enemy', when: [{ type: 'not', cond: { type: 'moving' } }] },
    { ability: HR.soulflay, target: 'enemy', when: [{ type: 'moving' }, { type: 'resource', resource: 'tithes', atLeast: 1 }] },
    { ability: HR.rotcurse, target: 'enemy', when: [{ type: 'moving' }] },
  ],
};

/** Spec id → kit. */
export const ACOLYTE_SPEC_KITS: Readonly<Record<string, ClassKit>> = { lightbinder: lightbinderKit, hemomancer: hemomancerKit, heretic: hereticKit };
